/**
 * Fill — web port of iOS `client-native/.../activities/Fill.tsx`.
 *
 * When the host threads the source video clip (`data.youtubeId` + `data.start`/
 * `data.end`), Fill renders iOS's staged chat reveal (Fill.tsx:60-147,243-296):
 *   TypingIndicator → left "fill in the blank!" prompt bubble → left video
 *   bubble (parrot avatar + segment-looped ActivityPlayer) → left sentence
 *   bubble (with the "_____" blank) → centered option pills. On correct, a
 *   right-side user bubble reveals the completed sentence (Fill.tsx:288-296).
 * Without a `youtubeId` the component falls back to the original static
 * sentence + pills layout (back-compat for callers not yet threading video).
 *
 * Retry-until-correct (§4): wrong tap → red flash + shake → reset after 500ms,
 * "Try again!" hint shown, the correct answer is NEVER revealed. Correct →
 * green flash →
 *   - feed: auto onFinish() after ~800ms (iOS Fill feed holds 800ms — Fill.tsx
 *     181-185, unlike MCQ/Write/Match which advance immediately).
 *   - lesson: 600ms → CorrectDrawer (confirm-to-continue) → onFinish(false)
 *     (iOS Fill:187 + CorrectDrawer 347-353).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ActivityButton, {
  type ActivityButtonRef,
  type ActivityButtonState,
} from "../ui/ActivityButton";
import CorrectDrawer from "../ui/CorrectDrawer";
import TypingIndicator from "../ui/TypingIndicator";
import { colors } from "@/ui";
import { callWithAuth, getUserInfo } from "@/api/Api";
import { resolveProfileAvatarSrc } from "@/components/Profile/avatarUtils";
import ActivityPlayer, {
  type ActivityPlayerHandle,
} from "@/components/Activities/ActivityPlayer";
import parrotAvatar from "@/features/tutorial/assets/parrot-method.png";
import type { ActivityComponentContract, FillData } from "../types";
import {
  activityContainerStyle,
  buttonsGridStyle,
  gridCellStyle,
  titleStyle,
} from "./layout";

const WRONG_RESET_MS = 500;
const FEED_FINISH_DELAY_MS = 800;
/** Lesson: hold the green flash before sliding up the CorrectDrawer (iOS 600ms). */
const LESSON_CORRECT_DRAWER_DELAY_MS = 600;

// Staged chat-reveal timing (iOS Fill.tsx:60-64).
const TYPING_DURATION = 1200;
const BUBBLE_1_DELAY = 1300;
const BUBBLE_VIDEO_DELAY = 1900;
const BUBBLE_SENTENCE_DELAY = 2500;
const PILLS_DELAY = 3100;

const CHAT_AVATAR = 38;
const chatAvatarStyle: React.CSSProperties = {
  width: CHAT_AVATAR,
  height: CHAT_AVATAR,
  borderRadius: CHAT_AVATAR / 2,
  objectFit: "cover",
  marginBottom: 2,
  flexShrink: 0,
};

/** Left assistant row: parrot avatar + bubble, bottom-aligned (iOS ChatBubble
 *  side="left"). Matches the video bubble's avatar so all coach messages line up. */
function AssistantRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        alignSelf: "flex-start",
        // Cap the avatar+bubble row to the column width so a maxed-out bubble
        // (280) + 38 avatar + 8 gap + 16 padding can't overflow narrow PWA
        // viewports (iPhone SE ~320px); the bubble (minWidth:0) shrinks instead.
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        padding: "0 8px",
      }}
    >
      <img src={parrotAvatar} alt="" style={chatAvatarStyle} />
      {children}
    </div>
  );
}

function UserRow({
  avatarUrl,
  children,
}: {
  avatarUrl: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        alignSelf: "flex-end",
        // Same overflow cap as AssistantRow (see note there).
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        padding: "0 8px",
      }}
    >
      {children}
      <img src={avatarUrl} alt="" style={chatAvatarStyle} />
    </div>
  );
}

export interface FillProps extends ActivityComponentContract {
  data: FillData;
  /** Listening-time telemetry forwarded to the video bubble's player. */
  onTrackListeningTime?: (time: number) => void;
}

export default function Fill({
  data,
  variant = "lesson",
  onFinish,
  onTrackAttempt,
  onTrackListeningTime,
}: FillProps) {
  const isFeed = variant === "feed";
  const hasVideo = !!(data?.youtubeId || data?.cloudflareId);
  const firstName = data?.firstName || "there";

  // The learner's profile avatar for the reply bubble — resolved the SAME way
  // the Profile screen does (per-user localStorage bird/photo, else the default
  // bird from their name), NOT the server `avatarUrl` field, which is empty for
  // users who picked a bird avatar. Reads the shared ["userInfo"] query (same
  // source the host uses for firstName) for the userId, so it dedupes with the
  // lesson's existing fetch.
  const { data: userInfo } = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  const userAvatarUrl = useMemo(
    () => resolveProfileAvatarSrc(userInfo?.id, userInfo?.firstName),
    [userInfo?.id, userInfo?.firstName],
  );

  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [buttonStates, setButtonStates] = useState<
    Record<number, ActivityButtonState>
  >({});
  const [showTryAgain, setShowTryAgain] = useState(false);
  const [showCorrect, setShowCorrect] = useState(false);
  const [showCorrectDrawer, setShowCorrectDrawer] = useState(false);
  const buttonRefs = useRef<Record<number, ActivityButtonRef | null>>({});
  const videoRef = useRef<ActivityPlayerHandle>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Per-activity attempt counter (1,2,3…) → attempt_number / is_first_attempt.
  const attemptRef = useRef(0);

  // Staged chat reveal (only meaningful when there's a video to sequence).
  const [showTyping, setShowTyping] = useState(true);
  const [showBubble1, setShowBubble1] = useState(false);
  const [showVideoBubble, setShowVideoBubble] = useState(false);
  const [showSentenceBubble, setShowSentenceBubble] = useState(false);
  const [showPills, setShowPills] = useState(false);

  useEffect(() => {
    if (!hasVideo) {
      // No video to sequence — reveal everything immediately (legacy layout).
      setShowTyping(false);
      setShowBubble1(true);
      setShowSentenceBubble(true);
      setShowPills(true);
      return;
    }
    const t1 = window.setTimeout(() => setShowTyping(false), TYPING_DURATION);
    const t2 = window.setTimeout(() => setShowBubble1(true), BUBBLE_1_DELAY);
    const t3 = window.setTimeout(
      () => setShowVideoBubble(true),
      BUBBLE_VIDEO_DELAY,
    );
    const t4 = window.setTimeout(
      () => setShowSentenceBubble(true),
      BUBBLE_SENTENCE_DELAY,
    );
    const t5 = window.setTimeout(() => setShowPills(true), PILLS_DELAY);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
      window.clearTimeout(t5);
    };
  }, [hasVideo]);

  // iOS Fill.tsx:129-132 flips `isPlaying` true at the video-bubble reveal
  // (`play={isPlaying}`) so the looping clip auto-plays. The web player only
  // mounts once `showVideoBubble` is true, so start it imperatively after mount.
  useEffect(() => {
    if (showVideoBubble) videoRef.current?.play();
  }, [showVideoBubble]);

  const options = useMemo(() => data?.options ?? [], [data]);

  // Split the sentence on the blank so we can render the gap inline.
  const parts = useMemo(() => {
    const segs = (data?.question ?? "").split(/_{3,}/);
    return [segs[0] ?? "", segs[1] ?? ""];
  }, [data]);

  const correctAnswer = useMemo(
    () => options.find((o) => o.correct)?.choice ?? "",
    [options],
  );
  const completedSentence = `${parts[0]}${correctAnswer}${parts[1]}`;

  const handleClick = (correct: boolean, index: number) => {
    if (clickedIndex !== null) return;
    setClickedIndex(index);

    if (correct) {
      onTrackAttempt?.("fill", true, ++attemptRef.current);
      setShowTryAgain(false);
      setShowCorrect(true);
      buttonRefs.current[index]?.triggerCorrect();
      setButtonStates((p) => ({ ...p, [index]: "matched" }));
      window.setTimeout(
        () => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }),
        150,
      );
      if (isFeed) {
        window.setTimeout(() => {
          setClickedIndex(null);
          onFinish();
        }, FEED_FINISH_DELAY_MS);
      } else {
        // Lesson: hold the green flash, then slide up the CorrectDrawer.
        window.setTimeout(
          () => setShowCorrectDrawer(true),
          LESSON_CORRECT_DRAWER_DELAY_MS,
        );
      }
      return;
    }

    onTrackAttempt?.("fill", false, ++attemptRef.current);
    buttonRefs.current[index]?.triggerIncorrect();
    setButtonStates((p) => ({ ...p, [index]: "incorrect" }));
    setShowTryAgain(true);
    window.setTimeout(() => {
      setButtonStates((p) => ({ ...p, [index]: "default" }));
      setClickedIndex(null);
    }, WRONG_RESET_MS);
  };

  if (!data) {
    return (
      <div style={activityContainerStyle(isFeed)}>
        <span style={{ color: isFeed ? colors.white : colors.secondary }}>
          Loading…
        </span>
      </div>
    );
  }

  // The inline "____" sentence — shared between the chat bubble (video mode)
  // and the centered card (legacy mode).
  const sentenceInline = (
    <>
      <span>{parts[0]}</span>
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 60,
          // Sit the blank on the text baseline like a write-in line. The bubble
          // is a flex row with `alignItems:center`, which would otherwise float
          // this bar through the vertical middle of the words (reads like a
          // strikethrough); `alignSelf:flex-end` + a full line-box height drops
          // the underline to the bottom of the line so it reads as a blank.
          alignSelf: "flex-end",
          height: "1.1em",
          borderBottom: `2px solid ${colors.primary}`,
          margin: "0 4px",
        }}
      />
      <span>{parts[1]}</span>
    </>
  );

  return (
    <div style={activityContainerStyle(isFeed)}>
      {!hasVideo && <div style={titleStyle(isFeed)}>Fill in the blank</div>}

      {/* Chat / sentence area (scrollable) */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: hasVideo ? "flex-start" : "center",
          gap: 14,
          padding: hasVideo ? "12px 8px 16px" : 0,
        }}
      >
        {hasVideo && showTyping && <TypingIndicator isFeed={isFeed} />}

        {hasVideo && showBubble1 && (
          <AssistantRow>
            <div style={promptBubbleStyle(isFeed)}>
              Hey {firstName}, fill in the blank!
            </div>
          </AssistantRow>
        )}

        {hasVideo && showVideoBubble && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 8,
              padding: "0 8px",
            }}
          >
            <img
              src={parrotAvatar}
              alt=""
              style={{
                width: 38,
                height: 38,
                borderRadius: 19,
                objectFit: "cover",
                marginBottom: 2,
              }}
            />
            <div
              style={{
                flex: 1,
                // Cap the bubble so a wide/desktop viewport can't blow the
                // video up and push the sentence + options below the fold —
                // the question must stay visible without scrolling at any width.
                maxWidth: 260,
                aspectRatio: "1.5",
                borderRadius: 12,
                overflow: "hidden",
                backgroundColor: colors.black,
              }}
            >
              <ActivityPlayer
                ref={videoRef}
                youtubeId={data.youtubeId ?? ""}
                cloudflareId={data.cloudflareId}
                player={data.player}
                onTimeUpdate={() => {}}
                startTime={data.start ?? 0}
                endTime={data.end ?? 0}
                onTrackListeningTime={onTrackListeningTime}
                onPlay={() => {}}
              />
            </div>
          </div>
        )}

        {hasVideo
          ? showSentenceBubble && (
              <AssistantRow>
                <div
                  style={{
                    ...promptBubbleStyle(isFeed),
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  {sentenceInline}
                </div>
              </AssistantRow>
            )
          : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    backgroundColor: isFeed
                      ? colors.greyChatBubble
                      : colors.white,
                    borderRadius: 16,
                    padding: "16px 20px",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 4,
                    color: colors.primary,
                    fontWeight: 500,
                    fontSize: "1.125rem",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    maxWidth: "100%",
                  }}
                >
                  {sentenceInline}
                </div>
              </div>
            )}

        {/* Correct → reveal the completed sentence as a right-side user bubble,
            with the learner's avatar after it (iOS ChatBubble side="right"). */}
        {hasVideo && showCorrect && (
          <UserRow avatarUrl={userAvatarUrl}>
            <div
              style={{
                backgroundColor: colors.blue,
                color: colors.white,
                borderRadius: 16,
                padding: "12px 16px",
                fontWeight: 600,
                fontSize: "1rem",
                maxWidth: 280,
                // Shrink below 280 inside the width-capped row (no overflow on
                // narrow viewports); the completed sentence wraps instead.
                minWidth: 0,
              }}
            >
              {completedSentence}
            </div>
          </UserRow>
        )}
      </div>

      <div
        style={{
          opacity: showPills ? 1 : 0,
          transition: "opacity 300ms ease",
          // Block taps until the staged reveal finishes — the pills are only
          // opacity:0 (still mounted for the fade), so without this learners
          // could answer before the prompt/video/sentence appear (iOS only
          // mounts the buttons once showPills is set).
          pointerEvents: showPills ? "auto" : "none",
        }}
      >
        {showTryAgain && (
          <div
            style={{
              textAlign: "center",
              color: colors.error,
              fontWeight: 600,
              fontSize: "0.875rem",
              marginBottom: 8,
            }}
          >
            Try again!
          </div>
        )}
        <div style={buttonsGridStyle}>
          {options.map((option, index) => (
            <div key={`${option.choice}-${index}`} style={gridCellStyle}>
              <ActivityButton
                ref={(r) => {
                  buttonRefs.current[index] = r;
                }}
                text={option.choice}
                variant={variant}
                disabled={clickedIndex !== null && clickedIndex !== index}
                state={buttonStates[index] ?? "default"}
                correctAnimationHoldMs={isFeed ? undefined : null}
                onPress={() => handleClick(option.correct, index)}
              />
            </div>
          ))}
        </div>
      </div>

      {!isFeed && (
        <CorrectDrawer
          visible={showCorrectDrawer}
          hideTranslation
          onContinue={() => {
            setShowCorrectDrawer(false);
            setClickedIndex(null);
            onFinish(false);
          }}
        />
      )}
    </div>
  );
}

/** Left chat-bubble style, variant-aware (iOS ChatBubble side="left"). */
function promptBubbleStyle(isFeed: boolean): React.CSSProperties {
  return {
    backgroundColor: isFeed ? colors.greyChatBubble : colors.white,
    color: colors.primary,
    borderRadius: 16,
    padding: "12px 16px",
    fontWeight: 500,
    fontSize: "1rem",
    boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
    maxWidth: 280,
    // Allow the bubble to shrink below 280 inside the width-capped row so the
    // avatar + bubble never overflow narrow viewports (text wraps instead).
    minWidth: 0,
  };
}

